// GraphQL configuration and utilities
// Use environment variables for dynamic configuration
// export const GRAPHQL_URL = import.meta.env.VITE_GRAPHQL_URL || 'http://localhost:8000/order';
export const GRAPHQL_URL = 'https://apidev29logis.us-fx.pro/order';  //live

export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
  }>;
}

export interface OrderListingResponse {
  orderListing: {
    data: Array<{
      id: string;
      reference: string;
      order_date: string;
      status: string;
      payment_status: string;
      customer: {
        email: string;
        phone: string;
      };
      market_place_type: {
        name: string;
      };
      city: {
        name: string;
      };
      order_products_count: number;
      order_products: Array<{
        id: string;
        code: string;
        first_identifier: string;
        second_identifier: string;
        marketplace_order_product: {
          name: string;
        };
        marketplace: {
          name: string;
        };
      }>;
    }>;
    paginatorInfo: {
      count: number;
      currentPage: number;
      firstItem: number;
      hasMorePages: boolean;
      lastItem: number;
      lastPage: number;
      perPage: number;
      total: number;
    };
  };
}

// GraphQL request function
export async function graphqlRequest<T = any>(
  query: string,
  variables: Record<string, any> = {},
  token?: string,
  storeId?: number
): Promise<GraphQLResponse<T>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (storeId) {
    headers['X-Store-ID'] = storeId.toString();
  }

  try {
    const response = await fetch(GRAPHQL_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        query,
        variables,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (data.errors) {
      console.error('GraphQL Errors:', data.errors);
      return {
        errors: data.errors,
      };
    }

    return {
      data: data.data,
    };
  } catch (error) {
    console.error('GraphQL Request Error:', error);
    return {
      errors: [
        {
          message: error instanceof Error ? error.message : 'Network error occurred',
        },
      ],
    };
  }
}

// GraphQL Queries
export const ORDER_LISTING_QUERY = `
  query OrderListing(
    $status: String
    $search: String
    $city: String
    $marketPlaceType: String
    $customerEmail: String
    $orderDateFrom: DateTime
    $orderDateTo: DateTime
    $includeTestOrder: Boolean
    $page: Int
    $first: Int
    $sort: String
    $order: String
  ) {
    orderListing(
      status: $status
      search: $search
      city: $city
      marketPlaceType: $marketPlaceType
      customerEmail: $customerEmail
      orderDateFrom: $orderDateFrom
      orderDateTo: $orderDateTo
      includeTestOrder: $includeTestOrder
      page: $page
      first: $first
      sort: $sort
      order: $order
    ) {
      data {
        id
        reference
        order_date
        status
        payment_status
        customer {
          email
          phone
        }
        market_place_type {
          name
        }
        city {
          name
        }
        order_products_count
        order_products {
          id
          code
          first_identifier
          second_identifier
          marketplace_order_product {
            name
            status
          }
          marketplace {
            name
          }
        }
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

// GraphQL API functions
export const graphqlApi = {
  getOrderListing: async (
    token: string,
    storeId: number,
    variables: {
      status?: string;
      search?: string;
      city?: string;
      marketPlaceType?: string;
      customerEmail?: string;
      orderDateFrom?: string;
      orderDateTo?: string;
      includeTestOrder?: boolean;
      page?: number;
      first?: number;
      sort?: string;
      order?: string;
    } = {}
  ): Promise<GraphQLResponse<OrderListingResponse>> => {
    return graphqlRequest<OrderListingResponse>(
      ORDER_LISTING_QUERY,
      variables,
      token,
      storeId
    );
  },
};

// Status mapping for GraphQL
export const STATUS_MAPPING = {
  // Operation page statuses
  'awaiting': 'on-its-way',
  'in_facility': 'delivered-to-facility',
  'destination_warehouse': 'received-at-main-warehouse',
  'distribution_center': 'received-at-distribution-center',
  'delivered': 'delivered-to-the-customer',
  
  // Fulfillment statuses (from Helper::getStatusWiseOrderItem)
  'outstanding': 'outstanding',
  'payment_verification': 'payment-verified',
  'payment_verified': 'payment-verified',
  'pending_item_completion': 'pending-item-completion',
  'item_completion_payment_unverified': 'item-completion-payment-unverified',
  'item_completion_unverified': 'item-completion-payment-unverified',
  'item_completion_payment_verified': 'item-completion-payment-verified',
  'prepare_for_procurement': 'prepare-for-procurement',
  'procured_items': 'procured-items',
  'unsettled_items': 'unsettled-items',
  'under_review': 'under-review',
  'partial_fulfillment': 'partial-fulfillment',
  'refunded': 'refunded',
  'not_eligible_refund': 'not-eligible-refund',
  'on_its_way': 'on-its-way',
  'delayed': 'delayed',
  'unreceived': 'unreceived',
  'procured_but_not_delivered': 'procured-but-not-delivered-to-facility',
  'delivered_to_facility': 'delivered-to-facility',
  'shipped_to_destination_warehouse': 'shipped-to-destination-warehouse',
  'received_at_main_warehouse': 'received-at-main-warehouse',
  'shipped_to_distribution_center': 'shipped-to-distribution-center',
  'received_at_distribution_center': 'received-at-distribution-center',
  'delivered_to_the_customer': 'delivered-to-the-customer',
  
  // Legacy mappings (keeping for backward compatibility)
  'ready_for_procurement': 'prepare-for-procurement',
  'fulfilled': 'item-completion-payment-verified',
  'awaiting_resolution': 'under-review',
  'shipped_to_distribution': 'shipped-to-distribution-center',
  'received_at_warehouse': 'received-at-main-warehouse',
  
  // Awaiting Resolution subtab mappings
  'canceled': 'canceled',
  'unsettled-items': 'unsettled-items',
  'partial-fulfillment': 'partial-fulfillment',
};

// Helper function to map frontend status to GraphQL status
export function mapStatusToGraphQL(status: string): string {
  return STATUS_MAPPING[status as keyof typeof STATUS_MAPPING] || status;
}
